import dayjs, { Dayjs } from "dayjs";
import { z } from "zod";
import { refinements } from "@helpers/refinements";
import i18n from "@/i18n";
import { BadgeStatus } from "@/types/general";

const ORDER_PRICE_PRECISION = import.meta.env.VITE_ORDER_PRICE_PRECISION;

const validDateRefinement = refinements.validDate;

const maxDecimalPrecisionRefinement = refinements.maxDecimalPrecision(ORDER_PRICE_PRECISION);

const positiveNumberRefinement = refinements.positive;

const lineSchema = z.object({
    id: z.number().nullish(),
    comment: z.string().max(90).nullish(),
    product: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
        barcode: z.string().nullish(),
        is_service: z.boolean(),
        in_stock: z.coerce.number().nullish(),
        // min_sale_quantity: z.coerce.number().nullish(),
        prices: z.object({
            // purchase_price: z.number(),
            selling_price: z.number(),
            // Contribution margin inputs (WEB-423), summed by the order SummaryBlock.
            list_price: z.number().nullish(),
            extra_cost: z.number().nullish(),
        }),
        category: z
            .object({
                id: z.number(),
                name: z.string(),
                code: z.string().nullish(),
            })
            .nullish(),
        has_bom: z.boolean(),
        // is_component: z.boolean(),
    }),
    quantity: z.coerce.number().positive().min(0),
    unit_price: z.coerce
        .number()
        .refine(positiveNumberRefinement.refine, positiveNumberRefinement.message)
        .refine(maxDecimalPrecisionRefinement.refine, maxDecimalPrecisionRefinement.message),
    discount: z.coerce.number().min(0).max(100).optional(),
    tax: z
        .object({
            id: z.number(),
            name: z.string().nullish(),
            rate: z.number(),
        })
        .nullish(),
    availableQuantity: z.number().nullish(),

    // TODO: this field may be extended in the future to be a globally available feature, but for now it is only for 24SO
    alternative_name: z.string().nullish(),
});

const groupSchema = z.object({
    id: z.number().nullish(),
    name: z.string(),
    comment: z.string().nullish(),
    module: z.union([z.literal("bom"), z.literal("collection"), z.literal("custom")]),
    module_entity_id: z.number().nullish(),
    lines: z.array(lineSchema),
});

const lineGroupUnion: z.ZodType<LineSchema | GroupSchema> = z.lazy(() =>
    z.union([lineSchema, groupSchema])
);

const reservationSchema = z.object({
    product: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
    }),
    quantity: z.coerce.number().min(0),
});

export const saleOrdersScheme = z
    .object({
        mode: z.union([z.literal("creation"), z.literal("editing")]),
        status: z.union([
            z.literal(BadgeStatus.Base.offer),
            z.literal(BadgeStatus.Base.new),
            z.literal(BadgeStatus.Base.prepaid),
        ]),

        is_invoiced: z.boolean(),
        is_editable: z.boolean(),

        // ==== Customer ====
        customer: z.object({
            id: z.number(),
            name: z.string(),
            code: z.string().nullish(),
        }),

        order_date: z
            .instanceof(dayjs as unknown as typeof Dayjs)
            .refine(validDateRefinement.refine, validDateRefinement.message),
        preferred_delivery_date: z.instanceof(dayjs as unknown as typeof Dayjs).nullish(),
        language: z.object({ id: z.number(), name: z.string(), code: z.string() }).nullish(),

        // ==== References ====
        our_reference: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),
        reference_number: z.object({
            id: z.number().nullish(),
            code: z.string().trim().nullish(),
        }),
        their_reference: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),
        their_reference_name: z.string().trim().max(255).nullish(),
        payment_terms: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),
        delivery_terms: z
            .object({
                id: z.number(),
                name: z.string(),
            })
            .nullish(),

        // ==== Addresses ====
        billing_address: z
            .object({
                name: z.string(),
                street: z.string().nullish(),
                city: z.string().nullish(),
                country: z
                    .object({
                        id: z.number(),
                        name: z.string(),
                        code: z.string(),
                    })
                    .nullish(),
            })
            .nullish(),

        delivery_address: z
            .object({
                id: z.number(),
                name: z.string(),
                street: z.string().nullish(),
                city: z.string().nullish(),
                country: z
                    .object({
                        id: z.number(),
                        name: z.string(),
                        code: z.string(),
                    })
                    .nullish(),
            })
            .nullish(),

        is_billing_for_delivery: z.boolean(),
        comment: z.string().nullish(),

        // ==== Products ====
        lines: z.array(lineGroupUnion).min(1, {
            message: i18n.t("general.validation.global.noProductsAdded"),
        }),

        reservations: z.array(reservationSchema).optional(),
        reservations_expiration_date: z.instanceof(dayjs as unknown as typeof Dayjs).nullish(),

        deleted_lines: z.array(z.number().nullish()).nullish(),

        twenty_four_seven_office: z
            .object({
                project: z.custom<{ id: string; name: string }>().nullish(),
            })
            .nullish(),
    })
    .superRefine((data, ctx) => {
        if (!data.is_billing_for_delivery && !data.delivery_address) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.required",
                },
                path: ["delivery_address"],
            });
        }

        if (
            data.preferred_delivery_date &&
            data.preferred_delivery_date.isBefore(data.order_date, "day")
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.invalidPreferredDeliveryDate",
                },
                path: ["preferred_delivery_date"],
            });
        }
    });

export type LineGroupUnion = z.infer<typeof lineGroupUnion>;
export type GroupSchema = z.infer<typeof groupSchema>;
export type LineSchema = z.infer<typeof lineSchema>;
export type SaleOrderSubmitIntent =
    | "save_order"
    | "save_offer"
    | "save_prepaid"
    | "save_and_pick"
    | "edit_save";

export function isGroup(union: LineGroupUnion): union is GroupSchema {
    return union && "module" in union && "name" in union && "lines" in union;
}

export function isLine(union: LineGroupUnion): union is LineSchema {
    return union && "product" in union && "quantity" in union;
}

export type SaleOrdersFormTyping = z.infer<typeof saleOrdersScheme>;
export type ReservationSchema = z.infer<typeof reservationSchema>;

export type SOProduct = z.infer<typeof lineSchema>["product"];
